// Shared priority-color + helper utilities for event views (Calendar, Table,
// Cards, Year, Map). Keeping them here prevents drift between views.

export const PRIORITY_COLORS = {
  // Priority 1: Accepted applications - green (most actionable)
  accepted: {
    bg: 'bg-emerald-100',
    border: 'border-l-emerald-600',
    text: 'text-emerald-700',
    dot: 'bg-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700 border-emerald-400',
    banner: 'bg-emerald-50 border-emerald-200',
    label: 'Accepted',
  },
  // Priority 2: In Progress applications (draft, pending, submitted)
  applicationInProgress: {
    bg: 'bg-purple-100',
    border: 'border-l-purple-600',
    text: 'text-purple-700',
    dot: 'bg-purple-600',
    badge: 'bg-purple-100 text-purple-700 border-purple-300',
    banner: 'bg-purple-50 border-purple-200',
    label: 'Application In Progress',
  },
  // Priority 3: Waitlisted applications - orange (pending outcome)
  waitlisted: {
    bg: 'bg-orange-100',
    border: 'border-l-orange-500',
    text: 'text-orange-700',
    dot: 'bg-orange-500',
    badge: 'bg-orange-100 text-orange-700 border-orange-400',
    banner: 'bg-orange-50 border-orange-200',
    label: 'Waitlisted',
  },
  // Priority 4: Rejected applications - rose (outcome decided)
  rejected: {
    bg: 'bg-rose-100',
    border: 'border-l-rose-500',
    text: 'text-rose-700',
    dot: 'bg-rose-500',
    badge: 'bg-rose-100 text-rose-700 border-rose-400',
    banner: 'bg-rose-50 border-rose-200',
    label: 'Rejected',
  },
  // Priority 5: Saved/Favorited events
  saved: {
    bg: 'bg-red-100',
    border: 'border-l-red-500',
    text: 'text-red-600',
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-600 border-red-300',
    banner: 'bg-red-50 border-red-200',
    label: 'Saved Event',
  },
  // Priority 6: My Events (user created)
  myEvent: {
    bg: 'bg-gray-100',
    border: 'border-l-gray-500',
    text: 'text-gray-600',
    dot: 'bg-gray-500',
    badge: 'bg-gray-100 text-gray-600 border-gray-300',
    banner: 'bg-gray-50 border-gray-200',
    label: 'My Event',
  },
  // Priority 7: Community events (backup options)
  community: {
    bg: 'bg-cyan-100',
    border: 'border-l-cyan-500',
    text: 'text-cyan-700',
    dot: 'bg-cyan-500',
    badge: 'bg-cyan-100 text-cyan-700 border-cyan-400',
    banner: 'bg-cyan-50 border-cyan-200',
    label: 'Community Event',
  },
  // Deadline indicator (can be combined with any priority)
  deadline: {
    bg: 'bg-[#FE5F55]/15',
    border: 'border-l-[#FE5F55]',
    text: 'text-[#c44a42]',
    dot: 'bg-[#FE5F55]',
    badge: 'bg-red-100 text-red-700 border-red-300',
    banner: 'bg-red-50 border-red-200',
    label: 'Deadline Soon',
  },
};

// Legacy alias — older code referenced this name.
export const PILL_COLORS = PRIORITY_COLORS;

// Return {level, type, status} where level 1 == highest priority. Used for sorting.
export const getEventPriority = (event, applicationStatusMap, favoriteIds) => {
  const appStatus = applicationStatusMap ? applicationStatusMap[event.id] : null;
  const isFavorited = favoriteIds?.has(event.id);
  const isMyEvent = event.is_user_event;
  const isCommunity = event.is_community;

  if (appStatus) {
    if (appStatus.status === 'accepted')
      return { level: 1, type: 'accepted', status: appStatus.status };
    if (['draft', 'pending', 'submitted'].includes(appStatus.status))
      return { level: 2, type: 'applicationInProgress', status: appStatus.status };
    if (appStatus.status === 'waitlisted')
      return { level: 3, type: 'waitlisted', status: appStatus.status };
    if (appStatus.status === 'rejected')
      return { level: 4, type: 'rejected', status: appStatus.status };
  }
  if (isCommunity) return { level: 7, type: 'community', status: null };
  if (isFavorited && !isMyEvent) return { level: 5, type: 'saved', status: null };
  if (isMyEvent) return { level: 6, type: 'myEvent', status: null };
  return { level: 8, type: 'community', status: null };
};

// Short alias for views that only need the type (not level/status).
export const getEventPriorityType = (event, applicationStatusMap, favoriteIds) =>
  getEventPriority(event, applicationStatusMap, favoriteIds).type;

export const sortEventsByPriority = (events, applicationStatusMap, favoriteIds) => {
  return [...events].sort((a, b) => {
    const pa = getEventPriority(a, applicationStatusMap, favoriteIds);
    const pb = getEventPriority(b, applicationStatusMap, favoriteIds);
    if (pa.level !== pb.level) return pa.level - pb.level;
    const da = a.date ? new Date(a.date + 'T00:00:00') : new Date('9999-12-31');
    const db = b.date ? new Date(b.date + 'T00:00:00') : new Date('9999-12-31');
    return da - db;
  });
};
